import {
  Children,
  cloneElement,
  type ComponentType,
  createContext,
  type FC,
  type PropsWithChildren,
  type ReactElement,
  useContext,
  useMemo,
} from 'react';
import { type GeolocationInfo } from '@cx-web/api-clients/geoip-api/types';
import { type SiteConfig } from '@cx-web/api-clients/site-config-api/types';
import LabelService, { type ILabelActions } from '@cx-web/feature-i18n';
import TrackingActions, { type ITrackingActions } from '@cx-web/feature-tracking';

import { type Country, type Repub } from '@cx-modules/constants';
import { type StateCode } from '@cx-modules/types';

export type ServicesProviderProps = {
  NODE_ENV: 'development' | 'production' | 'test';
  geolocationInfo: GeolocationInfo;
  labels: Record<string, string>;
  repub: Repub;
  siteConfig: SiteConfig;
};

export type ServicesProviderData = {
  DEV_ENV: boolean;
  NODE_ENV: 'production' | 'development' | 'test';
  countryCode: Country;
  geolocationInfo: GeolocationInfo;
  labelActions: ILabelActions;
  repub: Repub;
  siteConfig: SiteConfig;
  state: StateCode | '';
  trackingActions: ITrackingActions;
};

// Context
const ServicesContext = createContext<ServicesProviderData>(undefined as any as ServicesProviderData);
ServicesContext.displayName = 'ServicesContext';

// Provider
const ServicesProvider: FC<PropsWithChildren<ServicesProviderProps>> = ({
  NODE_ENV,
  children,
  geolocationInfo,
  labels,
  repub,
  siteConfig,
  ...props
}) => {
  const { countryCode, subdivisionCode: state } = geolocationInfo;
  const DEV_ENV = NODE_ENV !== 'production';

  const labelActions = useMemo(() => new LabelService(DEV_ENV, labels), [DEV_ENV, labels]);
  const trackingActions = useMemo(() => new TrackingActions(), []);

  const value = useMemo(
    () => ({
      DEV_ENV,
      NODE_ENV,
      siteConfig,
      labelActions,
      trackingActions,
      labels,
      repub,
      countryCode,
      geolocationInfo,
      state,
    }),
    [DEV_ENV, NODE_ENV, siteConfig, labelActions, trackingActions, labels, countryCode, geolocationInfo, state, repub],
  );
  return (
    <ServicesContext.Provider value={value} {...props}>
      {children}
    </ServicesContext.Provider>
  );
};

const useServicesContext = () => {
  const servicesContext = useContext(ServicesContext);
  if (!servicesContext) {
    throw new Error('useServicesContext can only be used inside of ServicesProvider');
  }

  return servicesContext;
};

const withServicesContextCC = ({ children }: PropsWithChildren) => {
  return (
    <ServicesContext.Consumer>
      {servicesContext => {
        return Children.map(children, child => {
          const item = child as ReactElement;

          if (typeof item.type === 'string') {
            return item;
          }

          return cloneElement(item, {
            context: servicesContext,
          });
        });
      }}
    </ServicesContext.Consumer>
  );
};

function withServicesContextHOC<T extends ServicesProviderData>(Component: ComponentType<T>) {
  const ComponentWithServicesContext = (props: Omit<T, keyof ServicesProviderData>) => (
    <ServicesContext.Consumer>
      {servicesContext => <Component {...(props as any as T)} context={servicesContext} />}
    </ServicesContext.Consumer>
  );

  const displayName = Component.displayName || Component.name || 'Component';
  ComponentWithServicesContext.displayName = `withServicesContextHOC(${displayName})`;

  return ComponentWithServicesContext;
}

const ServicesProviderWrapper: FC<PropsWithChildren<ServicesProviderProps>> = ({
  NODE_ENV,
  children,
  geolocationInfo,
  labels,
  repub,
  siteConfig,
}) => {
  return (
    <ServicesProvider
      NODE_ENV={NODE_ENV}
      geolocationInfo={geolocationInfo}
      labels={labels}
      repub={repub}
      siteConfig={siteConfig}
    >
      {children}
    </ServicesProvider>
  );
};

export {
  ServicesContext,
  useServicesContext,
  withServicesContextCC,
  withServicesContextHOC,
  ServicesProviderWrapper as ServicesProvider,
};
